import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["linkComponent", "$active", "$hasChildren", "$hovered", "$responsive", "$depth", "$isFolderNav", "$cssStyles"],
  _excluded2 = ["active", "children", "count", "disabled", "defaultOpen", "duration", "external", "href", "hovered", "iconLeft", "iconRight", "onBlur", "onClick", "onOpenChange", "open", "role", "title", "statusDot", "value", "linkComponent", "tabIndex", "to"],
  _excluded3 = ["onMouseEnter", "onMouseLeave"],
  _excluded4 = ["hovered"];
import { Children, isValidElement } from 'react';
import { Flex } from '../../../components/Flex';
import styled from 'foundations-theming/utils';
import { View } from '../../../components/View';
import { activeStyles, parentStyles, linkStyles, hoveredStyles, disabledStyles } from './SideNavItem.helpers';
import { SideNavContextProvider, useSideNavContext } from './SideNavContext';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { StatusDot } from '../StatusDot';
import { useFolderNavContext } from '../FolderNav/FolderNavContext';
import { filterProps } from '../../../utils/filterProps';
import { useHovered } from '../../utils/useHovered';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { uniqueId } from '../../utils/uniqueId';
import { AccordionTransition, AccordionTransitionWrapper } from '../AccordionTransition';
import { NAV_LIST_TRANSITION_TIMING } from '../../../constants/times';
import { isRenderable } from '../../utils/functions';
import devLogger from 'react-utils/devLogger';
import { IconHolder } from '../IconHolder';
import { getTarget, getRel } from '../Link/Link.utils';
import { getFocusStyle } from '../../utils/focus/focus.style';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const expandableContentId = uniqueId('SideNavItem-expandable-content-');
const StyledUl = styled.ul.withConfig({
  displayName: "SideNavItem__StyledUl",
  componentId: "rqifwq-0"
})(["list-style:none;padding-inline-start:0;margin-block-end:", ";"], ({
  theme
}) => theme.space['100']);
const SideNavItemLabel = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    children: children
  });
};
const StyledCustomLink = styled(props => {
  const {
      linkComponent: Link
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(Link, Object.assign({}, rest));
}).withConfig({
  displayName: "SideNavItem__StyledCustomLink",
  componentId: "rqifwq-1"
})(["", " ", " &&{", ";}", " &&{", ";}", " padding-left:", "px;", ""], linkStyles, ({
  $active
}) => $active ? activeStyles : undefined, ({
  $hovered,
  disabled
}) => $hovered && !disabled ? hoveredStyles : undefined, ({
  theme
}) => getFocusStyle({
  theme
}), ({
  disabled
}) => disabled ? disabledStyles : undefined, ({
  $hasChildren
}) => $hasChildren ? parentStyles : undefined, ({
  theme,
  $depth
}) => parseInt(theme.space['300'], 10) * ($depth + 1), ({
  $cssStyles
}) => $cssStyles);
export const SideNavItem = _ref => {
  let {
      active = false,
      children,
      count,
      disabled = false,
      defaultOpen,
      duration = parseInt(NAV_LIST_TRANSITION_TIMING, 10),
      external = false,
      href,
      iconLeft: IconLeft,
      iconRight: IconRight,
      onBlur,
      onClick,
      onOpenChange,
      open,
      role = 'button',
      title,
      statusDot,
      value: itemValue,
      linkComponent = 'a',
      tabIndex = 0,
      to
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
  const {
    tokenProps,
    domProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
      onMouseEnter,
      onMouseLeave
    } = domProps,
    otherDomProps = _objectWithoutPropertiesLoose(domProps, _excluded3);
  const {
    isFolderNav
  } = useFolderNavContext();
  const hasChildren = isRenderable(children);
  if (process.env.NODE_ENV !== 'production') {
    if (to != null) {
      devLogger.warn({
        message: `SideNavItem: The \`to\` prop is a no-op. Did you mean \`href\`? (\`to="${to}"\`)`,
        key: 'SideNavItem: to'
      });
    }
  }
  const {
    value: navValue,
    depth
  } = useSideNavContext();
  const _useHovered = useHovered({
      onMouseEnter,
      onMouseLeave
    }),
    {
      hovered
    } = _useHovered,
    onMouseEventHandlers = _objectWithoutPropertiesLoose(_useHovered, _excluded4);
  const {
    isOpen,
    triggerElementAriaAttributes,
    onToggle
  } = useDisclosure({
    defaultIsOpen: defaultOpen,
    isOpen: open,
    onOpen: () => onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true),
    onClose: () => onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false)
  });
  const isActive = navValue !== undefined && itemValue !== undefined && navValue === itemValue ? true : active;
  const handleClick = evt => {
    if (disabled) {
      evt.stopPropagation();
      evt.preventDefault();
      return;
    }
    onToggle();
    onClick === null || onClick === void 0 || onClick(evt);
  };
  const handleKeyDown = evt => {
    const {
      key
    } = evt;
    if (key === ' ' || key === 'Space') {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      onToggle();
      handleClick(evt);

      // Prevent scrolling
      evt.preventDefault();
    }
  };
  const computedTarget = getTarget(external);
  const computedRel = getRel(undefined, computedTarget);
  return /*#__PURE__*/_jsxs("li", {
    children: [/*#__PURE__*/_jsxs(StyledCustomLink, Object.assign({
      linkComponent: linkComponent,
      $active: isActive,
      $depth: depth,
      $hasChildren: !!children,
      $hovered: hovered,
      $isFolderNav: isFolderNav,
      $responsive: false,
      $cssStyles: cssStyles,
      "aria-current": isActive,
      "aria-disabled": disabled,
      disabled: disabled,
      href: href,
      onBlur: onBlur
    }, onMouseEventHandlers, triggerElementAriaAttributes, {
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      role: href ? 'link' : role,
      tabIndex: disabled ? -1 : tabIndex,
      target: computedTarget,
      rel: computedRel
    }, otherDomProps, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        children: [statusDot && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineEnd: "100",
          children: /*#__PURE__*/_jsx(StatusDot, {
            use: statusDot
          })
        }), IconLeft && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineEnd: "100",
          children: typeof IconLeft === 'function' ? IconLeft() : IconLeft
        }), /*#__PURE__*/_jsx(SideNavItemLabel, {
          count: count,
          children: title
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        columnGap: "200",
        flexGrow: 1,
        justifyContent: "end",
        children: [count !== undefined && /*#__PURE__*/_jsx(View, {
          children: count
        }), IconRight && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          children: typeof IconRight === 'function' ? IconRight() : IconRight
        }), children && !isFolderNav && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(DownIcon, {
            size: "300",
            color: "icon-interactive-default"
          })
        })]
      }), external && /*#__PURE__*/_jsx(IconHolder, {
        icon: ExternalLinkIcon,
        color: "icon-core-default",
        size: "300",
        padding: "0",
        marginInlineStart: "100"
      })]
    })), /*#__PURE__*/_jsx(AccordionTransition, {
      id: expandableContentId,
      open: isOpen,
      duration: duration,
      Wrapper: AccordionTransitionWrapper,
      children: hasChildren && /*#__PURE__*/_jsx(StyledUl, {
        children: Children.map(children, child => {
          if ( /*#__PURE__*/isValidElement(child)) {
            return /*#__PURE__*/_jsx(SideNavContextProvider, {
              depth: depth + 1,
              value: navValue,
              children: child
            });
          }
        })
      })
    })]
  });
};